/**
 * Typed fetch client for the admin-api Worker.
 *
 * AUTH MODEL: this SPA sits BEHIND Cloudflare Access (Google IdP). The browser
 * already holds the Access cookie, so every request just needs
 * `credentials: 'include'` and the cookie rides along; admin-api verifies the
 * `Cf-Access-Jwt-Assertion` server-side. The SPA manages NO tokens.
 *
 * Every admin-api response is the envelope `{ ok, data?, error:{code,message}? }`.
 * `request()` unwraps it: returns `data` on ok, throws `ApiError` on `!ok` (or on
 * a non-JSON / network failure). 401 → caller should treat as not-signed-in
 * (Access cookie missing/expired → bounce to login).
 *
 * Base URL: `VITE_ADMIN_API_BASE` or same-origin `/api`.
 */
import type {
  Me,
  AdminUser,
  WhitelistEntry,
  ModelWhitelistEntry,
  Machine,
  Alert,
  AlertStatus,
  SoftwareLicense,
  LicenseBatch,
  SupportTicket,
  SupportTicketDetail,
  AuditEntry,
  AuditActorType,
  MetricsDaily,
  DashboardSummary,
  AnalyticsTimeline,
  AnalyticsTimelineParams,
  Role,
  AdminStatus,
  RosterEntry,
  RosterQuery,
  RosterFacets,
  RosterStats,
  RosterMetaPatch,
  Paged,
  ImportBatch,
  ImportRow,
  ImportPreview,
  ImportCommitBody,
  ImportShardResult,
  MachineLookupResult,
  LicenseStats,
  LicenseListQuery,
  RosterAnalytics,
  AuditQuery,
  TicketStatus,
} from '@/types';

/** admin-api bulk-revoke cap per call (workers/admin-api/src/roster_admin.ts BULK_MAX). */
export const BULK_REVOKE_MAX = 200;

const BASE = (import.meta.env.VITE_ADMIN_API_BASE || '/api').replace(/\/+$/, '');

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  /** Structured context of a partial failure (e.g. bulk_revoke_incomplete → { revoked, missing }). */
  readonly details?: Record<string, unknown>;
  constructor(code: string, message: string, status: number, details?: Record<string, unknown>) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.details = details;
  }
}

export const isApiError = (e: unknown): e is ApiError => e instanceof ApiError;
/** 401 from admin-api means the Access cookie is missing/expired. */
export const isUnauthorized = (e: unknown): e is ApiError =>
  isApiError(e) && e.status === 401;

interface Envelope<T> {
  ok: boolean;
  data?: T;
  error?: { code: string; message: string; details?: Record<string, unknown> };
}

type Query = Record<string, string | number | boolean | undefined | null>;

function withQuery(path: string, query?: Query): string {
  if (!query) return path;
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v !== undefined && v !== null && v !== '') usp.set(k, String(v));
  }
  const qs = usp.toString();
  return qs ? `${path}?${qs}` : path;
}

async function request<T>(
  method: string,
  path: string,
  opts: { body?: unknown; query?: Query } = {},
): Promise<T> {
  const url = BASE + withQuery(path, opts.query);
  let res: Response;
  try {
    res = await fetch(url, {
      method,
      credentials: 'include', // Access cookie rides along
      headers: opts.body !== undefined ? { 'content-type': 'application/json' } : undefined,
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    });
  } catch (e) {
    throw new ApiError('network', e instanceof Error ? e.message : 'network error', 0);
  }

  let env: Envelope<T> | null = null;
  try {
    env = (await res.json()) as Envelope<T>;
  } catch {
    // Non-JSON body (e.g. an Access login HTML redirect, or a gateway error).
    throw new ApiError(
      res.status === 401 ? 'unauthorized' : 'bad_response',
      `unexpected ${res.status} response from ${path}`,
      res.status,
    );
  }

  if (!env.ok) {
    const err = env.error ?? { code: 'unknown', message: `request failed (${res.status})` };
    throw new ApiError(err.code, err.message, res.status, err.details);
  }
  return env.data as T;
}

const get = <T>(path: string, query?: Query) => request<T>('GET', path, { query });
const post = <T>(path: string, body?: unknown, query?: Query) =>
  request<T>('POST', path, { body, query });

// List endpoints wrap their array under a named key (e.g. { entries: [...] } / { machines: [...] } /
// { alerts: [...] } / { tickets: [...] } / { metrics: [...] }). getList unwraps it to the bare array the
// components consume; a missing/non-array value → [] so an un-warmed table (e.g. metrics before the first
// cron run) renders an empty state instead of crashing the page (the `l.map is not a function` black screen).
const getList = <T>(path: string, key: string, query?: Query): Promise<T[]> =>
  get<Record<string, unknown>>(path, query).then((r) => {
    const v = r ? r[key] : undefined;
    return Array.isArray(v) ? (v as T[]) : [];
  });

const enc = encodeURIComponent;

// CSV exports are streamed by the Worker (content-disposition: attachment) and are opened as a
// plain <a href download> so the browser attaches the Access cookie — never through request(),
// which would try to JSON-parse a CSV body.
const exportUrl = (path: string, query?: object) => BASE + withQuery(path, query as Query | undefined);

// ────────────────────────────────────────────────────────────────────────────
// Typed method per endpoint. Page agents: use these names VERBATIM.
// ────────────────────────────────────────────────────────────────────────────
export const api = {
  // ── identity / RBAC ──
  me: () => get<Me>('/me'),
  requestAccess: (display_name?: string) =>
    post<Me>('/request-access', { display_name }),
  listAdminUsers: () => getList<AdminUser>('/admin-users', 'entries'),
  setAdminRole: (email: string, role: Role | null, status?: AdminStatus) =>
    post<AdminUser>(`/admin-users/${enc(email)}/role`, { role, status }),

  // ── whitelist (L3 BiosSN roster) ──
  // Legacy unpaged list (Worker keeps LIMIT 5000 when neither page nor limit is sent). The v2
  // roster page uses listRoster; this stays for the wire-contract lock and old callers.
  listWhitelist: () => getList<WhitelistEntry>('/whitelist', 'entries'),
  addWhitelist: (body: { sn: string; owner_name?: string; owner_email?: string; team?: string; notes?: string; expires_at?: number | null }) =>
    post<WhitelistEntry>('/whitelist', body),
  revokeWhitelist: (sn: string, reason?: string) =>
    post<WhitelistEntry>(`/whitelist/${enc(sn)}/revoke`, { reason }),
  restoreWhitelist: (sn: string) =>
    post<WhitelistEntry>(`/whitelist/${enc(sn)}/restore`),

  // ── roster v2 (F-039): paged list / facets / stats / export / meta / expiry / bulk / import ──
  // Paged object ({ entries, total, page, limit }) → plain get. Always send page so the Worker
  // takes the paged branch (no page + no limit = legacy LIMIT 5000 for old Pages builds).
  listRoster: (query: RosterQuery) => get<Paged<RosterEntry>>('/whitelist', query as Query),
  rosterFacets: () => get<RosterFacets>('/whitelist/facets'),
  rosterStats: () => get<RosterStats>('/whitelist/stats'),
  rosterExportUrl: (query: Omit<RosterQuery, 'page' | 'limit'>) => exportUrl('/whitelist/export', query),
  setRosterMeta: (sn: string, patch: RosterMetaPatch) =>
    post<{ entry: RosterEntry }>(`/whitelist/${enc(sn)}/meta`, patch),
  setRosterExpiry: (sn: string, expires_at: number | null) =>
    post<{ entry: RosterEntry }>(`/whitelist/${enc(sn)}/expiry`, { expires_at }),
  // ≤ BULK_REVOKE_MAX SNs per call (the Worker 400s more); Roster's bulk dialog splits a larger
  // selection into sequential calls and sums them.
  bulkRevokeRoster: (sns: string[], reason?: string) =>
    post<{ revoked: number; missing: string[] }>('/whitelist/bulk-revoke', { sns, reason }),
  machineLookup: (q: string) => get<MachineLookupResult>('/machine-lookup', { q }),
  importPreview: (rows: ImportRow[]) => post<ImportPreview>('/whitelist/import/preview', { rows }),
  importCommit: (body: ImportCommitBody) => post<ImportShardResult>('/whitelist/import/commit', body),
  importContinue: (batch_id: string) => post<ImportShardResult>('/whitelist/import/continue', { batch_id }),
  listImportBatches: () => getList<ImportBatch>('/whitelist/import-batches', 'entries'),

  // ── model whitelist (L4 ModelCode) ──
  listModelWhitelist: () => getList<ModelWhitelistEntry>('/model-whitelist', 'entries'),
  setModelWhitelist: (body: { display_model: string; notes?: string }) =>
    post<ModelWhitelistEntry>('/model-whitelist', body),
  revokeModelWhitelist: (normalized: string) =>
    post<ModelWhitelistEntry>(`/model-whitelist/${enc(normalized)}/revoke`),
  restoreModelWhitelist: (normalized: string) =>
    post<ModelWhitelistEntry>(`/model-whitelist/${enc(normalized)}/restore`),

  // ── machines (device registry) ──
  listMachines: (status?: ApprovalQuery) => getList<Machine>('/machines', 'machines', { status }),
  approveMachine: (sn: string) => post<Machine>(`/machines/${enc(sn)}/approve`),
  rejectMachine: (sn: string) => post<Machine>(`/machines/${enc(sn)}/reject`),

  // ── alerts (leak detection) ──
  listAlerts: (status?: AlertStatus) => getList<Alert>('/alerts', 'alerts', { status }),
  setAlertStatus: (id: string, status: AlertStatus) =>
    post<Alert>(`/alerts/${enc(id)}/status`, { status }),
  setAlertNotes: (id: string, notes: string) =>
    post<Alert>(`/alerts/${enc(id)}/notes`, { notes }),

  // ── licenses (codes are secrets; redacted in lists) ──
  listLicenses: (query?: { state?: LicenseStateQuery; batch_id?: string }) =>
    getList<SoftwareLicense>('/licenses', 'entries', query),
  listLicensesPaged: (query: LicenseListQuery) => get<Paged<SoftwareLicense>>('/licenses', query as Query),
  licenseStats: () => get<LicenseStats>('/licenses/stats'),
  licensesExportUrl: (query: Omit<LicenseListQuery, 'page' | 'limit'>) => exportUrl('/licenses/export', query),
  listLicenseBatches: () => getList<LicenseBatch>('/license-batches', 'entries'),
  // idempotency_key is REQUIRED by the worker (prevents double-mint on retry); the worker returns the
  // bare { batch_id, codes } (NOT a LicenseBatch object) — the caller builds the display batch from the
  // form it just submitted (see Licenses.tsx). Codes are shown ONCE, so this must not throw.
  mintLicenses: (body: {
    partner: string; quantity: number; code_prefix?: string; ship_date?: string; notes?: string; idempotency_key: string;
  }) => post<{ batch_id: string; codes: string[] }>('/licenses/mint', body),
  revokeLicense: (code: string) => post<SoftwareLicense>(`/licenses/${enc(code)}/revoke`),

  // ── support ──
  listSupportTickets: (status?: TicketStatusQuery) =>
    getList<SupportTicket>('/support/tickets', 'tickets', { status }),
  getSupportTicket: (id: string) => get<SupportTicketDetail>(`/support/tickets/${enc(id)}`),
  setSupportTicketStatus: (id: string, status: TicketStatusQuery) =>
    post<SupportTicket>(`/support/tickets/${enc(id)}/status`, { status }),
  setSupportTicketAssignee: (id: string, assignee: string) =>
    post<SupportTicket>(`/support/tickets/${enc(id)}/assignee`, { assignee }),
  replySupportTicket: (id: string, text: string) =>
    post<SupportTicket>(`/support/tickets/${enc(id)}/reply`, { text }),
  // Internal note (author_type='internal_note') — never shown to the App user.
  addSupportNote: (id: string, text: string) =>
    post<unknown>(`/support/tickets/${enc(id)}/note`, { text }),
  /**
   * Direct download URL for a support attachment R2 key. The admin-api endpoint
   * STREAMS the bytes back through the Worker (content-disposition: attachment,
   * support+ gate enforced via the Access cookie) — it is NOT a presigned/JSON
   * url, so we link to it directly and the browser downloads with the Access
   * cookie attached (2026-06-20 audit CF-1: the old JSON {url} contract did not
   * exist on the wire, so attachments never resolved).
   */
  supportAttachmentUrl: (key: string) => `${BASE}/support/attachments?key=${enc(key)}`,

  // ── analytics (license/serial redemption timeline — real licensing events, NOT usage telemetry) ──
  // Single object (not a list): { days, overall, by_batch, summary }.
  analyticsTimeline: (params?: AnalyticsTimelineParams) =>
    get<AnalyticsTimeline>('/analytics/timeline', params as Query),
  // Roster growth per UTC day (added by origin / revoked / expired). days 1–365.
  analyticsRoster: (days: number) => get<RosterAnalytics>('/analytics/roster', { days }),

  // ── audit ──
  listAudit: (query?: { actor_type?: AuditActorType; limit?: number }) =>
    getList<AuditEntry>('/audit', 'entries', query),
  auditForTarget: (coll: string, id: string) =>
    getList<AuditEntry>('/audit/target', 'entries', { coll, id }),
  // With page → paged object; without page the Worker keeps the legacy bare list (listAudit above).
  listAuditPaged: (query: AuditQuery) => get<Paged<AuditEntry>>('/audit', query as Query),
  auditExportUrl: (query: Omit<AuditQuery, 'page' | 'limit'>) => exportUrl('/audit/export', query),

  // ── metrics + dashboard ──
  // Worker returns { metrics: [{ date, computed_at, payload: {machines,alerts,licenses,...} }] }.
  // Unwrap .metrics + flatten payload into the row so each item is a flat MetricsDaily (date + sub-objects
  // at top level, as the type + Insights expect). Empty (cron not run yet) → [] → empty state.
  recentMetrics: (days?: number) =>
    get<{ metrics?: Array<{ date: string; computed_at?: number; payload?: Record<string, unknown> }> }>(
      '/metrics/recent',
      { days },
    ).then((r) =>
      (Array.isArray(r?.metrics) ? r!.metrics : []).map(
        (m) => ({ date: m.date, computed_at: m.computed_at, ...(m.payload ?? {}) }) as unknown as MetricsDaily,
      ),
    ),
  // Worker returns nested counts { machines:{pending,approved,rejected}, alerts:{new},
  // support_tickets:{open,investigating,waiting_user,resolved,closed}, admin_users:{pending} }; map to
  // the flat DashboardSummary keys the counters read (+ the per-status ticket block, v2).
  dashboardSummary: () =>
    get<{
      machines?: { pending?: number; approved?: number; rejected?: number };
      alerts?: { new?: number };
      support_tickets?: Partial<Record<TicketStatus, number>>;
      admin_users?: { pending?: number };
    }>('/dashboard/summary').then((s) => ({
      new_alerts: s?.alerts?.new,
      open_tickets: s?.support_tickets?.open,
      pending_admins: s?.admin_users?.pending,
      pending_machines: s?.machines?.pending,
      machines_total:
        (s?.machines?.pending ?? 0) + (s?.machines?.approved ?? 0) + (s?.machines?.rejected ?? 0),
      ticket_counts: s?.support_tickets ? { ...s.support_tickets } : undefined,
    })) as Promise<DashboardSummary>,
};

// String unions accepted as query params (kept loose where the API tolerates it).
type ApprovalQuery = 'pending' | 'approved' | 'rejected';
type LicenseStateQuery = 'minted' | 'activated' | 'revoked';
type TicketStatusQuery = 'open' | 'investigating' | 'waiting_user' | 'resolved' | 'closed';

/** signOut: Cloudflare Access logout endpoint (full-page redirect). */
export function accessLogoutUrl(): string {
  return '/cdn-cgi/access/logout';
}
